// Copyright (c) 2023 embyt GmbH. All rights reserved.
// Author: Marc Planeck

import * as React from "react";
import { connect } from "react-redux";
import { FormattedMessage, defineMessages, IntlContext, IntlShape } from "react-intl";
import { Button, Dropdown } from "react-bootstrap";

import { isLocalhost, toDropdownItems, tabClickHandler, isValidEmail } from "guiutils";

import { IState } from "states/states";
import { IAction, ActionType, raiseAction, IActionTypeLogin } from "actions";
import { DataState, RootPath, UserStateAWSError } from "types";

import InputField, { eInputFieldType } from "view/comp/InputField";
import SignUpEntry from "view/comp/SignupEntry";

declare const IS_CLOUD: boolean;

interface ILoginFormProps {
  loginUsername?: string;
  isRfidLogin: boolean;
  loginDataState: DataState;
  userStateAWSFeedback: UserStateAWSError | undefined;
}

const mapStateToProps = (state: IState) => {
  const props: ILoginFormProps = {
    loginUsername: state.gui.login.email,
    isRfidLogin: state.router.rootPath === RootPath.RFID,
    loginDataState: state.scheduler.loginDataState,
    userStateAWSFeedback: state.gui.login.userStateAWSFeedback,
  };
  return props;
};

interface ILoginFormDispatch {
  onLoginClick: (username: string, password: string) => void;
  onForgotPwd: (username: string) => void;
}

const mapDispatchToProps = (dispatch: (action: IAction<any>) => void) => {
  const dispatchProps: ILoginFormDispatch = {
    onLoginClick: (username: string, password: string) => {
      const payload: IActionTypeLogin = { username, password };
      dispatch(raiseAction(ActionType.ACT_LOGIN_REQ, payload));
    },
    onForgotPwd: (username: string) => {
      dispatch(raiseAction(ActionType.ACT_FORGOT_PASSWORD, username));
    },
  };
  return dispatchProps;
};

const messages = defineMessages({
  msg_user_caption: { id: "Select user" },
  rfidInvalid: { id: "This charger card number is not allowed to login." },
  emailPlaceholder: { id: "e.g. jane@doe.com" },
  rfidPlaceholder: { id: "e.g. 04A1B2C3" },
  passwordPlaceholder: { id: "Please enter a password." },
});

interface ILoginFormState {
  username: string;
  password: string;
  isNfcReadingStarted?: boolean;
  showValidationError: boolean;
}

class LoginFormComponent extends React.Component<
  ILoginFormProps & ILoginFormDispatch,
  ILoginFormState
> {
  static contextType = IntlContext;
  declare context: IntlShape;

  state: ILoginFormState = {
    username: IS_CLOUD ? (this.props.loginUsername ?? "") : "operator",
    password: "",
    showValidationError: false,
  };

  public render() {
    const users = ["admin", "operator"];
    const userItems = toDropdownItems(
      users,
      "username",
      (_name, value) => this.setState({ username: value }),
      (item: string) => item,
    );
    const hasNfcReading = this.props.isRfidLogin && window.NDEFReader;

    return (
      <>
        <form autoComplete="on" onSubmit={(e) => e.preventDefault()}>
          <table style={{ width: "100%" }}>
            <tbody>
              <tr className="loginLabel">
                <td>
                  <label htmlFor={IS_CLOUD ? "username" : undefined}>
                    {this.props.isRfidLogin ?
                      <FormattedMessage id="RFID" />
                    : IS_CLOUD ?
                      <FormattedMessage id="Email" />
                    : <FormattedMessage id="User" />}
                  </label>
                </td>
              </tr>
              <tr>
                <td>
                  {IS_CLOUD ?
                    <InputField
                      dataState={this.props.loginDataState}
                      value={this.state.username}
                      name="username"
                      autoComplete="username"
                      onChange={(event) => {
                        this.setState({ username: event?.currentTarget.value });
                      }}
                      inputType={eInputFieldType.Text}
                      autoFocus
                      title={
                        (
                          this.props.isRfidLogin &&
                          (!this.isUsernameValid() || this.state.username.length === 0)
                        ) ?
                          messages.rfidInvalid
                        : undefined
                      }
                      errorText={
                        (
                          (this.props.loginDataState === DataState.SAVE_FAIL &&
                            (this.props.userStateAWSFeedback === UserStateAWSError.LOGIN_FAILED ||
                              this.props.userStateAWSFeedback ===
                                UserStateAWSError.USERNAME_MISSING ||
                              this.props.loginUsername === "")) ||
                          (this.state.showValidationError && !this.isUsernameValid())
                        ) ?
                          this.props.isRfidLogin ?
                            "Check your RFID"
                          : IS_CLOUD ?
                            "Check your Email Address"
                          : "Check User"
                        : undefined
                      }
                      placeholder={
                        this.props.isRfidLogin ?
                          messages.rfidPlaceholder
                        : messages.emailPlaceholder
                      }
                    />
                  : isLocalhost() ?
                    <>admin</>
                  : <Dropdown className="btn-group-sm">
                      <Dropdown.Toggle
                        variant="secondary"
                        id="dropdown-username"
                        size="sm"
                        disabled={this.props.loginDataState === DataState.SAVE_REQ}
                      >
                        {this.state.username ||
                          this.context.formatMessage(messages.msg_user_caption)}
                      </Dropdown.Toggle>
                      <Dropdown.Menu>{userItems}</Dropdown.Menu>
                    </Dropdown>
                  }
                  <div className="mb-2"></div>
                </td>
              </tr>
              {!this.props.isRfidLogin && (
                <>
                  <tr className="loginLabel">
                    <td>
                      <label htmlFor="password">
                        <FormattedMessage id="Password" />
                      </label>
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <InputField
                        dataState={this.props.loginDataState}
                        name="password"
                        autoComplete="current-password"
                        value={this.state.password}
                        onChange={(event) =>
                          this.setState({ password: event?.currentTarget.value })
                        }
                        inputType={eInputFieldType.Password}
                        errorText={
                          (
                            (this.props.loginDataState === DataState.SAVE_FAIL &&
                              (this.props.userStateAWSFeedback === UserStateAWSError.LOGIN_FAILED ||
                                this.props.userStateAWSFeedback ===
                                  UserStateAWSError.PASSWORD_MISSING ||
                                this.state.password === "")) ||
                            (this.state.showValidationError && !this.isPasswordValid())
                          ) ?
                            "Check your Password"
                          : undefined
                        }
                        placeholder={messages.passwordPlaceholder}
                      />
                    </td>
                  </tr>
                </>
              )}
            </tbody>
          </table>
          <div className="mt-3">
            <div className="button-row">
              {!IS_CLOUD && isLocalhost() && (
                <Button
                  id="cancel-login"
                  type="button"
                  variant="secondary"
                  onClick={(_evt) => window.location.reload()}
                  disabled={this.props.loginDataState === DataState.SAVE_REQ}
                >
                  <FormattedMessage id="Cancel" />
                </Button>
              )}
              <Button
                id="perform-login"
                type="submit"
                variant="primary"
                onClick={(event) => {
                  event.preventDefault();
                  const username = !IS_CLOUD && isLocalhost() ? "admin" : this.state.username;

                  if (this.isUsernameValid() && this.isPasswordValid()) {
                    this.props.onLoginClick(username, this.state.password);
                  } else {
                    this.setState({ showValidationError: true });
                  }
                }}
                disabled={this.props.loginDataState === DataState.SAVE_REQ}
              >
                {this.props.loginDataState === DataState.SAVE_REQ && (
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                    aria-hidden="true"
                  ></span>
                )}
                <FormattedMessage id="Perform Login" />
              </Button>
            </div>
          </div>
          {IS_CLOUD &&
            !this.props.isRfidLogin &&
            this.props.loginDataState !== DataState.SAVE_REQ && (
              <div className="forgotPassword mt-3">
                <span
                  tabIndex={0}
                  onKeyDown={tabClickHandler}
                  className="text-link"
                  onClick={() => this.props.onForgotPwd(this.state.username)}
                >
                  <FormattedMessage id="Forgot Password?" />
                </span>
              </div>
            )}
          {hasNfcReading && this.props.loginDataState !== DataState.SAVE_REQ && (
            <div className="mt-2">
              <Button
                id="scan-nfc"
                variant="outline-success"
                onClick={async () => await this.onScanNfcCard()}
                disabled={this.state.isNfcReadingStarted}
              >
                {this.state.isNfcReadingStarted && (
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                    aria-hidden="true"
                  ></span>
                )}
                {this.state.isNfcReadingStarted ?
                  <FormattedMessage id="Hold NFC card to reader." />
                : <FormattedMessage id="Scan NFC card" />}
              </Button>
            </div>
          )}
        </form>
        {IS_CLOUD && !this.props.isRfidLogin && <SignUpEntry username={this.state.username} />}
        {this.renderValidationAlert()}
      </>
    );
  }

  private renderValidationAlert() {
    if (!this.state.showValidationError) {
      return null;
    }
    if (!this.isUsernameValid()) {
      return (
        <div className="alert alert-danger mt-3">
          {this.props.isRfidLogin ?
            <FormattedMessage id="This charger card number is not allowed to login." />
          : <FormattedMessage id="Please enter a valid email address." />}
        </div>
      );
    }
    if (!this.isPasswordValid()) {
      return (
        <div className="alert alert-danger mt-3">
          <FormattedMessage id="Your password must be at least 8 characters long." />
        </div>
      );
    }
    return null;
  }

  private isUsernameValid() {
    if (IS_CLOUD && this.props.isRfidLogin) {
      // at least 6 hexadecimal digits, potentially with leading # for Etrel pins
      return /^#?[0-9a-fA-F]{6,}$/.test(this.state.username);
    }
    if (IS_CLOUD) {
      return isValidEmail(this.state.username);
    }
    return true;
  }

  private isPasswordValid() {
    // we do not duplicate password validation here and rely on the backend rules
    return true;
  }

  private async onScanNfcCard() {
    try {
      const ndef = new NDEFReader();
      await ndef.scan();
      console.log("Scan started");
      this.setState({ isNfcReadingStarted: true });
      ndef.addEventListener("readingerror", () => {
        console.log("NFC reading error.");
        this.setState({ isNfcReadingStarted: false });
      });
      ndef.addEventListener("reading", (event) => {
        const serialNumber = (event as NDEFReadingEvent).serialNumber;
        if (serialNumber) {
          this.setState({ username: serialNumber.replace(/:/g, "") });
        } else {
          console.log("NFC reading result has no serial number.", event);
        }
        this.setState({ isNfcReadingStarted: false });
      });
    } catch (error) {
      console.log("Scanning NFC error", error);
    }
  }
}

const LoginForm = connect(mapStateToProps, mapDispatchToProps)(LoginFormComponent);

export default LoginForm;
